import React, { useEffect, useState, useRef, useMemo } from "react";
import { 
  PlusCircle, 
  Trash2, 
  Edit2, 
  ImageIcon, 
  X, 
  Search,
  ChevronLeft,
  ChevronRight,
  Eye
} from "lucide-react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useNotifications } from "../contexts/NotificationContext";

export default function GalleryAdmin() {
  // State management
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  // Form states
  const [altText, setAltText] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("success");

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [animateModal, setAnimateModal] = useState(false);
  const [animatePage, setAnimatePage] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editImageSrc, setEditImageSrc] = useState("");
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [detailImage, setDetailImage] = useState(null);

  // Request count state
  const [requestCount, setRequestCount] = useState(0);
  const [previousRequestCount, setPreviousRequestCount] = useState(0);

  const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const modalRef = useRef(null);

  const navigate = useNavigate();
  const { addNotification } = useNotifications();

  const filteredImages = useMemo(() => 
    images.filter(image => 
      Object.values(image).some(
        value => 
          value && 
          value.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    ),
    [images, searchTerm]
  );

  const totalPages = Math.ceil(filteredImages.length / itemsPerPage);
  const paginatedImages = useMemo(
    () => filteredImages.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage
    ),
    [filteredImages, currentPage, itemsPerPage]
  );

  // Data fetching
  const fetchImages = async () => {
    setLoading(true);
    try {
      const res = await axios.get("https://aaapd.org.et/gallery_backend/api/getImages.php");
      setImages(res.data);
    } catch (err) {
      console.error("Error fetching images:", err);
      showMessage("Failed to fetch images.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setAnimatePage(true);
    fetchImages();
    fetchRequestCount();
    // Poll for request count every 30 seconds
    const interval = setInterval(fetchRequestCount, 30000);
    return () => clearInterval(interval);
  }, []);

  // Monitor for new gallery requests and send notifications
  useEffect(() => {
    if (requestCount > previousRequestCount && previousRequestCount > 0) {
      // New requests have arrived
      addNotification({
        type: 'gallery',
        title: 'New Gallery Request',
        message: `There are ${requestCount} pending gallery request${requestCount > 1 ? 's' : ''} awaiting approval`,
        data: {
          requestCount: requestCount
        }
      });
    }
    setPreviousRequestCount(requestCount);
  }, [requestCount, previousRequestCount, addNotification]);

  const fetchRequestCount = async () => {
    try {
      const res = await axios.get(
        "https://aaapd.org.et/gallery_backend/api/getGalleryImages.php"
      );
      if (Array.isArray(res.data)) {
        const pendingCount = res.data.filter(img => img.status === "pending").length;
        setRequestCount(pendingCount);
      }
    } catch (err) {
      console.error("Error fetching request count:", err);
    }
  };

  // Message handling
  const showMessage = (msg, type = "success") => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => setMessage(""), 5000);
  };

  // Modal handlers
  const openAddModal = () => {
    setIsEditing(false);
    setAltText("");
    setDescription("");
    setFile(null);
    setEditImageSrc("");
    setModalPosition({ x: 0, y: 0 });
    setIsModalOpen(true);
    setTimeout(() => setAnimateModal(true), 10);
  };

  const openEditModal = (image) => {
    setIsEditing(true);
    setEditId(image.id);
    setAltText(image.alt_text || "");
    setDescription(image.description || "");
    setEditImageSrc(image.src);
    setFile(null);
    setModalPosition({ x: 0, y: 0 });
    setIsModalOpen(true);
    setTimeout(() => setAnimateModal(true), 10);
  };

  const closeModal = () => {
    setAnimateModal(false);
    setTimeout(() => setIsModalOpen(false), 300);
  };

  const openDetailModal = (image) => {
    setDetailImage(image);
    setIsDetailModalOpen(true);
  };

  const closeDetailModal = () => {
    setIsDetailModalOpen(false);
    setDetailImage(null);
  };

  // CRUD operations
  const handleAddImage = async (e) => {
    e.preventDefault();
    if (!file) return showMessage("Please select a file to upload.", "error");

    const formData = new FormData();
    formData.append("image", file);
    formData.append("alt_text", altText);
    formData.append("description", description);

    try {
      const res = await axios.post(
        "https://aaapd.org.et/gallery_backend/api/addImage.php",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (res.data.status === "success") {
        showMessage(res.data.message || "Image added successfully!");
        closeModal();
        fetchImages();
      } else {
        throw new Error(res.data.message || "Failed to add image");
      }
    } catch (err) {
      console.error("Error adding image:", err);
      showMessage(err.response?.data?.message || "Failed to add image", "error");
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      let res;

      if (file) {
        const formData = new FormData();
        formData.append("id", editId);
        formData.append("alt_text", altText);
        formData.append("description", description);
        formData.append("image", file);

        res = await axios.post(
          "https://aaapd.org.et/gallery_backend/api/editImage.php",
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
      } else {
        res = await axios.post(
          "https://aaapd.org.et/gallery_backend/api/editImage.php",
          { id: editId, alt_text: altText, description }
        );
      }

      if (res.data.status === "success") {
        showMessage("Image updated successfully!");
        closeModal();
        fetchImages();
      } else {
        throw new Error(res.data.message || "Failed to update image");
      }
    } catch (err) {
      console.error("Error updating image:", err);
      showMessage(err.response?.data?.message || "Failed to update image", "error");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this image?")) return;

    try {
      const res = await axios.delete(
        `https://aaapd.org.et/gallery_backend/api/deleteImage.php?id=${id}`
      );

      // Check if deletion was successful (status code 200 or response data indicates success)
      if (res.status === 200 || res.data.status === "success" || res.data === "success" || res.data.message) {
        showMessage("Image deleted successfully!");
        fetchImages();
      } else {
        throw new Error(res.data.message || "Failed to delete image");
      }
    } catch (err) {
      console.error("Error deleting image:", err);
      // Only show error if it's actually a network error (not a successful deletion)
      if (err.response && err.response.status !== 200) {
        showMessage(err.response?.data?.message || "Failed to delete image", "error");
      } else {
        // If no error response, assume success and refresh
        showMessage("Image deleted successfully!");
        fetchImages();
      }
    }
  };

  // Modal drag handlers
  const handleMouseDown = (e) => {
    if (e.target.closest('input, textarea, button')) return;
    setIsDragging(true);
    dragStart.current = {
      x: e.clientX - modalPosition.x,
      y: e.clientY - modalPosition.y,
    };
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setModalPosition({
      x: e.clientX - dragStart.current.x,
      y: e.clientY - dragStart.current.y,
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Pagination
  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div
      className={`bg-gray-50 min-h-screen p-6 transition-opacity duration-1000 ease-in-out ${
        animatePage ? "opacity-100" : "opacity-0"
      }`}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
    >
      {/* Header */}
      <div className="max-w-7xl mx-auto">
        <header className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Gallery Management</h1>
            <p className="text-gray-600 mt-1">Manage and organize your gallery images</p>
          </div>
          <div className="flex gap-3">

<button
  onClick={() => navigate("/adminapprovalgallery")}
  className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2.5 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 relative"
>
  <span>Requests</span>
  {requestCount > 0 && (
    <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center animate-pulse">
      {requestCount}
    </span>
  )}
</button>

            <button
              onClick={openAddModal}
              className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-4 py-2.5 rounded-lg shadow-md hover:shadow-lg transition-all duration-200"
            >
              <PlusCircle className="w-5 h-5" /> 
              <span>Add New Image</span>
            </button>
          </div>
        </header>

        {/* Message */}
        {message && (
          <div 
            className={`mb-6 p-4 rounded-lg ${
              messageType === "error" 
                ? "bg-red-100 border-l-4 border-red-500 text-red-700" 
                : "bg-green-100 border-l-4 border-green-500 text-green-700"
            }`}
          >
            {message}
          </div>
        )}

        {/* Search and Filter */}
        <div className="bg-white p-4 rounded-lg shadow-sm mb-6">
          <div className="relative max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search images..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 sm:text-sm"
            />
          </div>
        </div>

        {/* Gallery Table */}
        <div className="bg-white shadow overflow-hidden rounded-lg">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Preview
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Alt Text
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="4" className="px-6 py-8 text-center">
                      <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-teal-500"></div>
                      </div>
                      <p className="mt-2 text-sm text-gray-500">Loading images...</p>
                    </td>
                  </tr>
                ) : paginatedImages.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="px-6 py-12 text-center">
                      <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">No images found</h3>
                      <p className="mt-1 text-sm text-gray-500">
                        {searchTerm 
                          ? "No images match your search. Try a different term."
                          : "Get started by uploading a new image."}
                      </p>
                      {!searchTerm && (
                        <div className="mt-6">
                          <button
                            onClick={openAddModal}
                            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                          >
                            <PlusCircle className="-ml-1 mr-2 h-5 w-5" />
                            Add Image
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ) : (
                  paginatedImages.map((image) => (
                    <tr key={image.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex-shrink-0 h-16 w-16">
                          <img
                            className="h-16 w-16 rounded-md object-cover border border-gray-200"
                            src={image.src}
                            alt={image.alt_text || 'Gallery image'}
                          />
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900 max-w-xs truncate">
                          {image.alt_text || <span className="text-gray-400">No alt text</span>}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-500 max-w-md truncate">
                          {image.description || <span className="text-gray-400">No description</span>}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => openDetailModal(image)}
                            className="text-teal-600 hover:text-teal-900 p-1.5 rounded-md hover:bg-teal-50 transition-colors"
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => openEditModal(image)}
                            className="text-blue-600 hover:text-blue-900 p-1.5 rounded-md hover:bg-blue-50 transition-colors"
                            title="Edit"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(image.id)}
                            className="text-red-600 hover:text-red-900 p-1.5 rounded-md hover:bg-red-50 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white px-6 py-3 flex items-center justify-between border-t border-gray-200">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
                  disabled={currentPage === 1}
                  className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                    currentPage === 1
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                    currentPage === totalPages
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to{' '}
                    <span className="font-medium">
                      {Math.min(currentPage * itemsPerPage, filteredImages.length)}
                    </span>{' '}
                    of <span className="font-medium">{filteredImages.length}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
                      disabled={currentPage === 1}
                      className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                        currentPage === 1
                          ? 'text-gray-300 cursor-not-allowed'
                          : 'text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      <span className="sr-only">Previous</span>
                      <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                    </button>
                    
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            currentPage === pageNum
                              ? 'z-10 bg-teal-50 border-teal-500 text-teal-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    
                    <button
                      onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                        currentPage === totalPages
                          ? 'text-gray-300 cursor-not-allowed'
                          : 'text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      <span className="sr-only">Next</span>
                      <ChevronRight className="h-5 w-5" aria-hidden="true" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Image Modal */}
        {isModalOpen && (
          <div
            className={`fixed inset-0 flex items-center justify-center z-50 transition-opacity duration-300 ${
              animateModal ? 'opacity-100' : 'opacity-0'
            }`}
            style={{ 
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(4px)',
              WebkitBackdropFilter: 'blur(4px)'
            }}
          >
            <div
              ref={modalRef}
              onMouseDown={handleMouseDown}
              style={{
                transform: `translate(${modalPosition.x}px, ${modalPosition.y}px)`,
              }}
              className={`bg-white rounded-xl shadow-2xl w-full max-w-sm mx-4 transform transition-all duration-300 ease-in-out ${
                animateModal ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
              } ${isDragging ? 'cursor-grabbing' : 'cursor-move'}`}
            >
              <div className="px-5 py-3 border-b border-gray-200 flex justify-between items-center">
                <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                  {isEditing ? (
                    <>
                      <Edit2 className="w-4 h-4 text-teal-600" /> Edit Image
                    </>
                  ) : (
                    <>
                      <PlusCircle className="w-4 h-4 text-teal-600" /> Add New Image
                    </>
                  )}
                </h3>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <form
                className="p-5 space-y-3"
                onSubmit={isEditing ? handleEdit : handleAddImage}
              >
                {isEditing && (
                  <>
                    <div className="flex justify-center bg-gray-50 rounded-lg p-2">
                      <img
                        src={editImageSrc}
                        alt="Preview"
                        className="max-h-32 max-w-full object-contain rounded border border-gray-200"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="block text-xs font-medium text-gray-600">
                        Replace Image (optional)
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => setFile(e.target.files[0])}
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-xs file:font-medium file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100"
                      />
                      {file && (
                        <p className="mt-1 text-xs text-gray-500">
                          Selected: {file.name} ({(file.size / 1024).toFixed(2)} KB)
                        </p>
                      )}
                    </div>
                  </>
                )}
                
                {!isEditing && (
                  <div className="space-y-1">
                    <label className="block text-xs font-medium text-gray-600">
                      Image File
                    </label>
                    <div className="mt-1 flex justify-center px-4 pt-4 pb-4 border-2 border-gray-300 border-dashed rounded-md">
                      <div className="space-y-1 text-center">
                        <div className="flex text-xs text-gray-600">
                          <label
                            htmlFor="file-upload"
                            className="relative cursor-pointer bg-white rounded-md font-medium text-teal-600 hover:text-teal-500 focus-within:outline-none"
                          >
                            <span>Upload a file</span>
                            <input
                              id="file-upload"
                              name="file-upload"
                              type="file"
                              className="sr-only"
                              onChange={(e) => setFile(e.target.files[0])}
                              accept="image/*"
                            />
                          </label>
                          <p className="pl-1">or drag and drop</p>
                        </div>
                        <p className="text-xs text-gray-500">
                          PNG, JPG, GIF up to 5MB
                        </p>
                      </div>
                    </div>
                    {file && (
                      <p className="mt-1 text-xs text-gray-500">
                        Selected: {file.name} ({(file.size / 1024).toFixed(2)} KB)
                      </p>
                    )}
                  </div>
                )}

                <div className="space-y-1">
                  <label
                    htmlFor="alt-text"
                    className="block text-xs font-medium text-gray-600"
                  >
                    Alt Text
                  </label>
                  <input
                    type="text"
                    id="alt-text"
                    value={altText}
                    onChange={(e) => setAltText(e.target.value)}
                    placeholder="Describe this image for accessibility"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 text-sm"
                  />
                </div>

                <div className="space-y-1">
                  <label
                    htmlFor="description"
                    className="block text-xs font-medium text-gray-600"
                  >
                    Description
                  </label>
                  <textarea
                    id="description"
                    rows={2}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Add a detailed description of this image"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 text-sm"
                  />
                </div>

                <div className="pt-3 flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="inline-flex justify-center py-1.5 px-3 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="inline-flex justify-center py-1.5 px-3 border border-transparent shadow-sm text-xs font-medium rounded-md text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                  >
                    {isEditing ? 'Save Changes' : 'Upload Image'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Detail Modal */}
        {isDetailModalOpen && detailImage && (
          <div
            className="fixed inset-0 flex items-center justify-center z-50"
            style={{ 
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(4px)',
              WebkitBackdropFilter: 'blur(4px)'
            }}
          >
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm mx-4">
              <div className="px-5 py-3 border-b border-gray-200 flex justify-between items-center">
                <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                  <Eye className="w-4 h-4 text-teal-600" />
                  Image Details
                </h3>
                <button
                  onClick={closeDetailModal}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="p-5 space-y-3">
                <div className="flex justify-center bg-gray-50 rounded-lg p-2">
                  <img
                    src={detailImage.src}
                    alt={detailImage.alt_text || 'Gallery image'}
                    className="max-h-32 max-w-full object-contain rounded border border-gray-200"
                  />
                </div>

                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-600">
                    Image ID
                  </label>
                  <p className="text-sm text-gray-900 bg-gray-50 px-2 py-1.5 rounded border border-gray-200">
                    {detailImage.id}
                  </p>
                </div>

                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-600">
                    Alt Text
                  </label>
                  <p className="text-sm text-gray-900 bg-gray-50 px-2 py-1.5 rounded border border-gray-200">
                    {detailImage.alt_text || <span className="text-gray-400 italic">No alt text</span>}
                  </p>
                </div>

                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-600">
                    Description
                  </label>
                  <p className="text-sm text-gray-900 bg-gray-50 px-2 py-1.5 rounded border border-gray-200 min-h-[50px]">
                    {detailImage.description || <span className="text-gray-400 italic">No description</span>}
                  </p>
                </div>

                <div className="pt-3 flex justify-end space-x-2">
                  <button
                    onClick={closeDetailModal}
                    className="inline-flex justify-center py-1.5 px-3 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => {
                      closeDetailModal();
                      openEditModal(detailImage);
                    }}
                    className="inline-flex items-center gap-1.5 py-1.5 px-3 border border-transparent shadow-sm text-xs font-medium rounded-md text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                  >
                    <Edit2 className="h-3.5 w-3.5" />
                    Edit
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}